import React, { useEffect, useRef } from "react";
import {
  useFieldArray,
  useFormContext,
  useFormState,
  useWatch,
} from "react-hook-form";

import StepHeader from "../StepHeader";
import EntryCard from "../EntryCard";
import RHFInput from "@/components/form/RHFInput";

const StepEducation = ({
  mandatoryFields = [],
  nextAttempt = 0,
  disabled = false,
}) => {
  const { control, trigger } = useFormContext();
  const hasAppended = useRef(false);

  const { errors } = useFormState({
    control,
    name: "education",
  });

  const isInstitutionRequired = mandatoryFields.some(
    (f) =>
      f === "education.institution" ||
      f === "institution",
  );

  const isDegreeRequired = mandatoryFields.some(
    (f) =>
      f === "education.degree" ||
      f === "degree",
  );

  const isFieldOfStudyRequired = mandatoryFields.some(
    (f) =>
      f === "education.field_of_study" ||
      f === "fieldOfStudy",
  );

  const isStartDateRequired = mandatoryFields.some(
    (f) =>
      f === "education.start_date" ||
      f === "startDate",
  );

  const isEndDateRequired = mandatoryFields.some(
    (f) =>
      f === "education.end_date" ||
      f === "endDate",
  );

  const isGradeRequired = mandatoryFields.some(
    (f) =>
      f === "education.grade" ||
      f === "grade",
  );

  const {
    fields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "education",
  });

  const watchedEducation = useWatch({
    control,
    name: "education",
  });

  /**
   * Date correctness belongs to the Zod schema.
   *
   * We only retrigger both fields whenever either date changes because the
   * validity of startDate and endDate depends on one another.
   */
  useEffect(() => {
    if (!fields.length) return;

    const dateFields = fields.flatMap((_, index) => [
      `education.${index}.startDate`,
      `education.${index}.endDate`,
    ]);

    trigger(dateFields);
  }, [
    watchedEducation,
    fields.length,
    trigger,
  ]);

  /**
   * On Next/Submit, open the first education card containing an error and
   * move focus to the first invalid field.
   */
  useEffect(() => {
    if (nextAttempt === 0) return;

    const timer = setTimeout(() => {
      const firstErrorIndex = fields.findIndex(
        (_, index) =>
          !!errors?.education?.[index],
      );

      if (firstErrorIndex === -1) {
        return;
      }

      const errorFields =
        errors?.education?.[
          firstErrorIndex
        ];

      const fieldOrder = [
        "institution",
        "degree",
        "fieldOfStudy",
        "startDate",
        "endDate",
        "grade",
      ];

      const firstErrorField =
        fieldOrder.find(
          (field) =>
            !!errorFields?.[field],
        );

      if (!firstErrorField) {
        return;
      }

      const fieldId =
        `education.${firstErrorIndex}.${firstErrorField}`;

      const el =
        document.getElementById(fieldId);

      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        el.focus({
          preventScroll: true,
        });
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [
    nextAttempt,
    errors,
    fields,
  ]);

  /**
   * Keep one empty education entry available when no education records exist.
   */
  useEffect(() => {
    if (
      fields.length === 0 &&
      !hasAppended.current
    ) {
      hasAppended.current = true;

      append({
        degree: "",
        institution: "",
        fieldOfStudy: "",
        startDate: "",
        endDate: "",
        grade: "",
      });
    }
  }, [append, fields.length]);

  const hasMandatoryEducationFields =
    isInstitutionRequired ||
    isDegreeRequired ||
    isFieldOfStudyRequired ||
    isStartDateRequired ||
    isEndDateRequired ||
    isGradeRequired;

  return (
    <div>
      <StepHeader
        title="Education"
        disabled={disabled}
        onAdd={() =>
          append({
            degree: "",
            institution: "",
            fieldOfStudy: "",
            startDate: "",
            endDate: "",
            grade: "",
          })
        }
      />

      <div className="space-y-6 px-5">
        {fields.map(
          (item, index) => {
            const currentEducation =
              watchedEducation?.[
                index
              ] ?? item;

            return (
              <EntryCard
                key={item.id}
                label={
                  currentEducation.institution
                }
                forceOpen={
                  !!errors?.education?.[
                    index
                  ]
                }
                forceOpenTrigger={
                  nextAttempt
                }
                defaultOpen={
                  !currentEducation.institution
                }
                canRemove={
                  hasMandatoryEducationFields
                    ? index !== 0
                    : fields.length > 1
                }
                onRemove={() =>
                  remove(index)
                }
                disabled={disabled}
              >
                <RHFInput
                  name={`education.${index}.institution`}
                  label="Institution / University"
                  placeholder="Enter your College/University"
                  required={
                    isInstitutionRequired
                  }
                  disabled={disabled}
                />

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <RHFInput
                    name={`education.${index}.degree`}
                    label="Degree / Qualification"
                    placeholder="Bachelor's Degree"
                    required={
                      isDegreeRequired
                    }
                    disabled={disabled}
                  />

                  <RHFInput
                    name={`education.${index}.fieldOfStudy`}
                    label="Field of Study"
                    placeholder="e.g. Computer Science"
                    required={
                      isFieldOfStudyRequired
                    }
                    disabled={disabled}
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <RHFInput
                    name={`education.${index}.startDate`}
                    label="Start Year"
                    placeholder="YYYY"
                    required={
                      isStartDateRequired
                    }
                    disabled={disabled}
                  />

                  <RHFInput
                    name={`education.${index}.endDate`}
                    label="End Date / Graduating Year"
                    placeholder="YYYY or Present"
                    required={
                      isEndDateRequired
                    }
                    disabled={disabled}
                  />
                </div>

                <RHFInput
                  name={`education.${index}.grade`}
                  label="Grade / GPA / Percentage"
                  placeholder="e.g. 8.5/10 | 3.5/4.0 | 75%"
                  required={
                    isGradeRequired
                  }
                  disabled={disabled}
                />
              </EntryCard>
            );
          },
        )}
      </div>
    </div>
  );
};

export default StepEducation;